import React, { useEffect, useRef, useState } from 'react';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import Slider from '@mui/material/Slider';
import Typography from '@mui/material/Typography';
import Stack from '@mui/material/Stack';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import PauseIcon from '@mui/icons-material/Pause';
import VolumeUpIcon from '@mui/icons-material/VolumeUp';
import VolumeOffIcon from '@mui/icons-material/VolumeOff';
import FullscreenIcon from '@mui/icons-material/Fullscreen';
import FastForwardIcon from '@mui/icons-material/FastForward';
import FastRewindIcon from '@mui/icons-material/FastRewind';

function formatTime(seconds) {
  if (!isFinite(seconds) || isNaN(seconds)) return '0:00';
  const s = Math.floor(seconds % 60).toString().padStart(2, '0');
  const m = Math.floor((seconds / 60) % 60).toString();
  const h = Math.floor(seconds / 3600);
  if (h > 0) {
    return `${h}:${m.padStart(2, '0')}:${s}`;
  }
  return `${m}:${s}`;
}

const CenteredVideoPlayer = ({
  src,
  poster,
  style,
  videoProps = {},
  fillParent = false,
  showControlsBar = true,
}) => {
  const videoRef = useRef(null);
  const containerRef = useRef(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isMuted, setIsMuted] = useState(false);
  const [duration, setDuration] = useState(0);
  const [currentTime, setCurrentTime] = useState(0);
  const [volume, setVolume] = useState(1);
  const [showControls, setShowControls] = useState(false);
  const [videoStarted, setVideoStarted] = useState(false);
  const videoStartedRef = useRef(false);
  const hideControlsTimeoutRef = useRef(null);
  const shouldAutoHideRef = useRef(false);
  const lastClickTimeRef = useRef(0);
  
  // Ensure controls is always false, even if passed in videoProps
  const { controls, ...restVideoProps } = videoProps;
  
  // Clear timeout on unmount
  useEffect(() => {
    return () => {
      if (hideControlsTimeoutRef.current) {
        clearTimeout(hideControlsTimeoutRef.current);
      }
    };
  }, []);
  
  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    const onLoaded = () => setDuration(video.duration || 0);
    const onTime = () => setCurrentTime(video.currentTime || 0);
    const onPlay = () => {
      setIsPlaying(true);
      setVideoStarted(true);
      videoStartedRef.current = true;
      // Hide controls when video starts playing
      if (hideControlsTimeoutRef.current) {
        clearTimeout(hideControlsTimeoutRef.current);
        hideControlsTimeoutRef.current = null;
      }
      shouldAutoHideRef.current = true;
      setShowControls(false);
    };
    const onPause = () => {
      setIsPlaying(false);
      // Show controls when video pauses (only if video has started)
      if (videoStartedRef.current) {
        if (hideControlsTimeoutRef.current) {
          clearTimeout(hideControlsTimeoutRef.current);
          hideControlsTimeoutRef.current = null;
        }
        shouldAutoHideRef.current = false;
        setShowControls(true);
      }
    };
    const onVolumeChange = () => {
      setVolume(video.volume);
      setIsMuted(video.muted);
    };

    video.addEventListener('loadedmetadata', onLoaded);
    video.addEventListener('timeupdate', onTime);
    video.addEventListener('play', onPlay);
    video.addEventListener('pause', onPause);
    video.addEventListener('volumechange', onVolumeChange);

    return () => {
      video.removeEventListener('loadedmetadata', onLoaded);
      video.removeEventListener('timeupdate', onTime);
      video.removeEventListener('play', onPlay);
      video.removeEventListener('pause', onPause);
      video.removeEventListener('volumechange', onVolumeChange);
    };
  }, []);
  
  // Handle initial play button click
  const handleInitialPlay = (e) => {
    e.preventDefault();
    e.stopPropagation();
    const video = videoRef.current;
    if (video) {
      video.play();
      setVideoStarted(true);
      videoStartedRef.current = true;
    }
  };

  // Handle click/touch to show/hide controls (only after video has started)
  const handleVideoClick = (e) => {
    // Don't handle if clicking on controls themselves or initial play button
    if (e.target.closest('.video-controls') || e.target.closest('.initial-play-button')) {
      return;
    }
    
    // Only handle clicks after video has started
    if (!videoStarted) {
      return;
    }
    
    // Prevent double-firing on touch devices (onClick and onTouchStart both fire)
    const now = Date.now();
    if (now - lastClickTimeRef.current < 300) {
      return;
    }
    lastClickTimeRef.current = now;
    
    // Prevent default video behavior
    e.preventDefault();
    e.stopPropagation();
    
    // Clear any existing timeout
    if (hideControlsTimeoutRef.current) {
      clearTimeout(hideControlsTimeoutRef.current);
      hideControlsTimeoutRef.current = null;
    }
    
    setShowControls(prev => {
      // If controls are currently hidden, show them
      if (!prev) {
        // If video is playing, show controls and auto-hide after 3 seconds
        if (isPlaying) {
          shouldAutoHideRef.current = true;
          hideControlsTimeoutRef.current = setTimeout(() => {
            // Only hide if we still should auto-hide (controls weren't manually hidden)
            if (shouldAutoHideRef.current) {
              setShowControls(false);
            }
          }, 3000);
        }
        return true;
      } else {
        // If controls are visible, hide them (user wants to hide them)
        shouldAutoHideRef.current = false;
        return false;
      }
    });
  };

  const togglePlay = () => {
    const video = videoRef.current;
    if (!video) return;
    if (video.paused) {
      video.play();
    } else {
      video.pause();
    }
  };

  const toggleMute = () => {
    const video = videoRef.current;
    if (!video) return;
    video.muted = !video.muted;
    setIsMuted(video.muted);
  };

  const handleSeek = (e, value) => {
    const video = videoRef.current;
    if (!video) return;
    const newTime = (Array.isArray(value) ? value[0] : value) * duration;
    video.currentTime = newTime;
    setCurrentTime(newTime);
  };

  const handleVolume = (e, value) => {
    const v = Array.isArray(value) ? value[0] : value;
    const video = videoRef.current;
    if (!video) return;
    video.volume = v;
    setVolume(v);
    if (v > 0 && isMuted) {
      video.muted = false;
      setIsMuted(false);
    }
  };

  const seekBy = (deltaSeconds) => {
    const video = videoRef.current;
    if (!video || !duration) return;
    const next = Math.min(Math.max(0, (video.currentTime || 0) + deltaSeconds), duration);
    video.currentTime = next;
    setCurrentTime(next);
  };

  const handleFullscreen = () => {
    const el = containerRef.current;
    if (!el) return;
    const anyEl = el;
    if (anyEl.requestFullscreen) anyEl.requestFullscreen();
    else if (anyEl.webkitRequestFullscreen) anyEl.webkitRequestFullscreen();
    else if (anyEl.mozRequestFullScreen) anyEl.mozRequestFullScreen();
    else if (anyEl.msRequestFullscreen) anyEl.msRequestFullscreen();
  };

  return (
    <Box 
      ref={containerRef} 
      sx={{ 
        position: fillParent ? 'absolute' : 'relative',
        top: fillParent ? 0 : undefined,
        left: fillParent ? 0 : undefined,
        width: '100%', 
        height: fillParent ? '100%' : undefined,
        backgroundColor: fillParent ? 'transparent' : '#000', 
        borderRadius: fillParent ? 0 : '8px', 
        overflow: 'hidden'
      }}
      onClick={handleVideoClick}
    >
      <video
        ref={videoRef}
        src={src}
        poster={fillParent ? undefined : poster}
        style={{
          ...(fillParent ? { position: 'absolute', top: 0, left: 0 } : {}),
          width: '100%',
          height: '100%',
          display: 'block',
          ...style
        }}
        playsInline
        preload="metadata"
        controls={false}
        disablePictureInPicture
        {...restVideoProps}
      />
      
      {/* Thumbnail cover – browsers don't always apply object-fit to <video poster>,
         so we render a real <img> that reliably fills the container with cover. */}
      {fillParent && poster && !videoStarted && (
        <img
          src={poster}
          alt=""
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            objectFit: 'cover',
            zIndex: 1,
            pointerEvents: 'none',
          }}
        />
      )}

      {/* Initial Play Button Overlay */}
      {!videoStarted && (
        <Box
          className="initial-play-button"
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            zIndex: 10000,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
          onClick={handleInitialPlay}
        >
          <IconButton
            aria-label="Play"
            sx={{
              color: '#fff',
              backgroundColor: 'rgba(0, 0, 0, 0.6)',
              width: 80,
              height: 80,
              '&:hover': {
                backgroundColor: 'rgba(0, 0, 0, 0.8)',
              },
              transition: 'all 0.3s ease',
            }}
          >
            <PlayArrowIcon sx={{ fontSize: 48 }} />
          </IconButton>
        </Box>
      )}
      
      {showControlsBar && <Box
        className="video-controls"
        sx={{
          position: 'absolute',
          left: '50%',
          bottom: '60px',
          transform: 'translateX(-50%)',
          background: 'linear-gradient(to top, rgba(0,0,0,0.65) 0%, rgba(0,0,0,0.55) 100%)',
          borderRadius: '10px',
          padding: '8px 14px',
          width: { xs: '96%', sm: '92%', md: '88%' },
          maxWidth: '900px',
          display: 'flex',
          flexDirection: 'column',
          gap: 1,
          pointerEvents: showControls ? 'auto' : 'none',
          zIndex: 9999,
          boxShadow: '0 8px 24px rgba(0,0,0,0.8)',
          border: '1px solid rgba(255,255,255,0.1)',
          opacity: showControls ? 1 : 0,
          transition: 'opacity 0.3s ease',
          visibility: showControls ? 'visible' : 'hidden',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Progress bar */}
        <Slider
          value={duration ? currentTime / duration : 0}
          onChange={handleSeek}
          step={0.001}
          min={0}
          max={1}
          sx={{ 
            color: '#fff',
            height: 6,
            '& .MuiSlider-thumb': {
              width: 14,
              height: 14,
              boxShadow: '0 2px 4px rgba(0,0,0,0.7)',
              backgroundColor: '#fff',
            },
            '& .MuiSlider-track': {
              height: 3,
              backgroundColor: '#fff',
            },
            '& .MuiSlider-rail': {
              height: 3,
              backgroundColor: 'rgba(255,255,255,0.3)',
            },
            '&:hover .MuiSlider-thumb': {
              width: 16,
              height: 16,
            },
          }}
        />
        
        {/* Control buttons */}
        <Stack direction="row" spacing={1} alignItems="center" sx={{ width: '100%', flexWrap: 'nowrap' }}>
          <IconButton 
            aria-label="Rewind 10 seconds" 
            onClick={() => seekBy(-10)} 
            size="small" 
            sx={{ color: '#fff', '&:hover': { backgroundColor: 'rgba(255,255,255,0.2)' }, padding: '4px' }}
          >
            <FastRewindIcon fontSize="small" />
          </IconButton>
          
          <IconButton 
            aria-label={isPlaying ? 'Pause' : 'Play'} 
            onClick={togglePlay} 
            size="medium" 
            sx={{ 
              color: '#fff', 
              backgroundColor: 'rgba(255,255,255,0.15)',
              width: 36,
              height: 36,
              '&:hover': { backgroundColor: 'rgba(255,255,255,0.25)' }
            }}
          >
            {isPlaying ? <PauseIcon /> : <PlayArrowIcon />}
          </IconButton>
          
          <IconButton 
            aria-label="Forward 10 seconds" 
            onClick={() => seekBy(10)} 
            size="small" 
            sx={{ color: '#fff', '&:hover': { backgroundColor: 'rgba(255,255,255,0.2)' }, padding: '4px' }}
          >
            <FastForwardIcon fontSize="small" />
          </IconButton>
          
          <Typography variant="caption" sx={{ color: '#fff', minWidth: 50, textAlign: 'right', fontSize: '0.75rem', fontWeight: 500 }}>
            {formatTime(currentTime)}
          </Typography>
          
          <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.6)', mx: 0.5, fontSize: '0.75rem' }}>
            /
          </Typography>
          
          <Typography variant="caption" sx={{ color: '#fff', minWidth: 50, fontSize: '0.75rem', fontWeight: 500 }}>
            {formatTime(duration)}
          </Typography>
          
          <Box sx={{ flex: 1 }} />
          
          <IconButton 
            aria-label={isMuted ? 'Unmute' : 'Mute'} 
            onClick={toggleMute} 
            size="small" 
            sx={{ color: '#fff', '&:hover': { backgroundColor: 'rgba(255,255,255,0.2)' }, padding: '4px' }}
          >
            {isMuted || volume === 0 ? <VolumeOffIcon fontSize="small" /> : <VolumeUpIcon fontSize="small" />}
          </IconButton>
          
          <Slider 
            value={volume} 
            onChange={handleVolume} 
            step={0.01} 
            min={0} 
            max={1} 
            sx={{ 
              color: '#fff', 
              width: 80,
              height: 4,
              '& .MuiSlider-thumb': {
                width: 10,
                height: 10,
                backgroundColor: '#fff',
              },
              '& .MuiSlider-track': {
                height: 2,
                backgroundColor: '#fff',
              },
              '& .MuiSlider-rail': {
                height: 2,
                backgroundColor: 'rgba(255,255,255,0.3)',
              },
            }} 
          />
          
          <IconButton 
            aria-label="Fullscreen" 
            onClick={handleFullscreen} 
            size="small" 
            sx={{ color: '#fff', '&:hover': { backgroundColor: 'rgba(255,255,255,0.2)' }, padding: '4px' }}
          >
            <FullscreenIcon fontSize="small" />
          </IconButton>
        </Stack>
      </Box>}
    </Box>
  );
};

export default CenteredVideoPlayer;

